import Image from 'next/image';

import { Box, Flex, Heading, Link, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import {
  AccreditationColorScheme,
  accreditationGradients,
  AccreditationStatsProps,
} from '~/features/accreditation/types';

export function AccreditationStatsSection({
  colorScheme = 'blue',
  heading,
  subheading,
  stats,
  sources,
}: { colorScheme?: AccreditationColorScheme } & AccreditationStatsProps) {
  const gradients = accreditationGradients[colorScheme];
  return (
    <ContentContainer as='section' contentMaxWidth='content-max-width-md' mobilePadding='0' overflow='hidden'>
      <Flex
        bgGradient={gradients.statsBg}
        borderRadius={{ base: '0px', md: '12px' }}
        color='white'
        flexDirection='column'
        gap={{ base: '24px', md: '40px' }}
        padding={{ base: '24px', md: '48px' }}
        position='relative'
      >
        <Box bottom={0} left={0} pointerEvents='none' position='absolute' right={0} top={0}>
          <Image
            alt=''
            loading='lazy'
            sizes='100vw'
            src='/images/accreditation/bg-stripes.png'
            style={{
              objectFit: 'contain',
              objectPosition: 'right',
              mixBlendMode: 'overlay',
            }}
            fill
          />
        </Box>
        <Flex flexDirection='column' gap='12px' position='relative'>
          <Heading as='h2' size={{ base: 'mobile-heading-3', md: 'heading-3' }}>
            {heading}
          </Heading>
          <Text color={`${colorScheme}.100`} size='md'>
            {subheading}
          </Text>
        </Flex>
        <Flex flexDirection={{ base: 'column', md: 'row' }} gap={{ base: '8px', md: '24px' }}>
          {stats.map((item) => (
            <Flex
              key={item.descriptionTop}
              bg='rgba(255, 255, 255, 0.24)'
              border='1px solid'
              borderColor='rgba(255, 255, 255, 0.24)'
              borderRadius='8px'
              flex={1}
              gap='20px'
              p={{ base: '16px', md: '28px' }}
            >
              <Box
                alignSelf='stretch'
                bgGradient={`linear(to-b, ${gradients.statsAccentStart}, ${gradients.statsAccentStart}00)`}
                w='2px'
              />
              <Flex
                flex={1}
                flexDirection={{ base: 'row', md: 'column' }}
                gap={{ base: '16px', md: '10px' }}
                alignItems={{
                  base: 'center',
                  md: 'flex-start',
                }}
              >
                <Heading as='p' fontSize={{ base: '2rem', md: '2.375rem' }} fontWeight='600' lineHeight='1'>
                  {item.number}
                  <Box as='span' fontSize={{ base: '1.375rem', md: '1.625rem' }} fontWeight='600'>
                    {item.suffixChar ?? '%'}
                  </Box>
                </Heading>
                <Box>
                  <Text fontSize='sm' fontWeight='600' lineHeight='1'>
                    {item.descriptionTop}
                  </Text>
                  <Text color={`${colorScheme}.100`} fontSize='sm'>
                    {item.descriptionBottom} <Suffix color='white' suffix={item.suffix} />
                  </Text>
                </Box>
              </Flex>
            </Flex>
          ))}
        </Flex>
      </Flex>
      <Flex
        flexDirection={{ base: 'column', md: 'row' }}
        gap={{ base: '12px', md: '32px' }}
        justifyContent='center'
        mt={{ base: '0', md: '24px' }}
        p={{ base: '20px', md: '0px' }}
      >
        {sources.map((source) => (
          <Flex key={source.suffix} alignItems='center' gap='4px'>
            <Suffix color='black' suffix={source.suffix} />
            <Link
              _visited={{ color: 'grey.500' }}
              color='grey.500'
              fontSize={{ base: 'xs', md: 'sm' }}
              fontWeight='400'
              href={source.href}
              isExternal
            >
              {source.label}
            </Link>
          </Flex>
        ))}
      </Flex>
    </ContentContainer>
  );
}

function Suffix({ suffix, color }: { suffix: number; color: string }) {
  return (
    <Flex
      alignItems='center'
      as='sup'
      border='solid 1px'
      borderColor={color}
      borderRadius='full'
      color={color}
      display='inline-flex'
      flexShrink={0}
      fontSize='7px'
      fontWeight='700'
      h='12px'
      justifyContent='center'
      w='12px'
    >
      {suffix}
    </Flex>
  );
}
